'use client';

import classNames from 'classnames';
import { JSX, RefObject, useState } from 'react';

import { Tooltip } from '@videoblocks/design-system';
import { View, ViewOff } from '@videoblocks/react-icons';

import { PASSWORD_MIN_LENGTH, PASSWORD_REQUIREMENTS } from '../../../lib/validation/passwordValidation';

export interface PasswordFieldProps {
    id: string;
    inputRef: RefObject<HTMLInputElement | null>;
    error: string;
    touched: boolean;
    focused: boolean;
    onChange: () => void;
    onFocus: () => void;
    onBlur: () => void;
    inputClassName: string;
    /** Kept apart from inputClassName so it is swapped for the error border rather than competing with it. */
    defaultBorderClassName: string;
    labelClassName: string;
}

export function PasswordField({
    id,
    inputRef,
    error,
    touched,
    focused,
    onChange,
    onFocus,
    onBlur,
    inputClassName,
    defaultBorderClassName,
    labelClassName,
}: PasswordFieldProps): JSX.Element {
    const [showPassword, setShowPassword] = useState(false);
    const hasError = touched && error !== '';
    // Sighted users see either the requirements list (focused) or the error (blurred), never both.
    // Assistive tech keeps the error while the field is focused so the user hears what to fix.
    const showError = !focused && hasError;
    const requirementsId = `${id}-requirements`;
    const errorId = `${id}-error`;

    return (
        <div>
            <label htmlFor={id} className={classNames('block', labelClassName)}>
                Password
            </label>
            <div className="relative">
                <input
                    ref={inputRef}
                    id={id}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
                    autoComplete="new-password"
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    aria-invalid={hasError || undefined}
                    aria-describedby={
                        [focused ? requirementsId : null, hasError ? errorId : null].filter(Boolean).join(' ') ||
                        undefined
                    }
                    data-testid="password-input"
                    className={classNames(
                        'pr-10',
                        inputClassName,
                        showError ? 'border-red-500' : defaultBorderClassName,
                    )}
                />
                <Tooltip
                    title={showPassword ? 'Hide password' : 'Show password'}
                    trigger={
                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setShowPassword((prev) => !prev);
                                }
                            }}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset focus:text-gray-700"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            tabIndex={0}
                        >
                            {showPassword ? <ViewOff className="h-4 w-4" /> : <View className="h-4 w-4" />}
                        </button>
                    }
                />
            </div>
            {focused && (
                <ul id={requirementsId} className="mt-1.5 list-disc pl-5 text-xs text-gray-600 space-y-0.5">
                    {PASSWORD_REQUIREMENTS.map((requirement) => (
                        <li key={requirement}>{requirement}</li>
                    ))}
                </ul>
            )}
            {hasError && (
                <div
                    id={errorId}
                    role="alert"
                    className={classNames('mt-1 text-sm text-red-600', { 'sr-only': !showError })}
                >
                    {error}
                </div>
            )}
        </div>
    );
}
